import { Box, styled, Typography } from "@material-ui/core";
import { Check, ChevronRight, PriorityHigh } from "@material-ui/icons";
import { EpocusBillingState } from "elephant-web/gql";
import { Link } from "react-router-dom";
import {
  BILLING_HOLD_REASON_LABELS,
  BILLING_MISSING_ITEM_LABELS,
} from "../../_lib/billing/billing-status-labels";
import {
  EPOCUS_BORDER,
  EPOCUS_SELECTED_BG,
  EPOCUS_STATUS_DRAFT_BG,
  EPOCUS_STATUS_DRAFT_TEXT,
  EPOCUS_STATUS_REVIEWED_BG,
  EPOCUS_STATUS_REVIEWED_TEXT,
  EPOCUS_SYNCED_BG,
  EPOCUS_SYNCED_TEXT,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../_lib/epocus-palette";
import { linkToEpocusBillingReport } from "../../links";
import type { EpocusBillingDayRowFieldsFragment } from "./__generated__/billing-operations.gql";
import {
  cptChipLabel,
  formatEstimateCents,
  MEDICARE_ESTIMATE_LABEL,
} from "./service-day";

const RowRoot = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "12px 16px",
  borderBottom: `1px solid ${EPOCUS_BORDER}`,
  minHeight: 56,
  "&:last-of-type": { borderBottom: "none" },
});

const RowLink = styled(Link)({
  textDecoration: "none",
  color: "inherit",
  display: "block",
  "&:hover": { backgroundColor: EPOCUS_SELECTED_BG },
});

const Mark = styled(Box)({
  width: 26,
  height: 26,
  borderRadius: 999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

const Info = styled(Box)({ flex: 1, minWidth: 0 });

const Chip = styled("span")({
  fontSize: rem(11.5),
  fontWeight: 700,
  padding: "3px 8px",
  borderRadius: 999,
  whiteSpace: "nowrap",
});

const Meta = styled(Typography)({
  fontSize: rem(12.5),
  color: EPOCUS_TEXT_SECONDARY,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const FeeRoot = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  flexShrink: 0,
});

const FeeCaption = styled(Typography)({
  fontSize: rem(11),
  color: EPOCUS_TEXT_SECONDARY,
  whiteSpace: "nowrap",
});

const StudyFee: FC<{
  estimate: EpocusBillingDayRowFieldsFragment["feeEstimate"];
}> = ({ estimate }) => {
  if (!estimate || estimate.needsProgramPostalCode) return null;

  const amount = estimate.amountCents ?? null;
  const unestimated = estimate.unestimatedLineCount;
  const caption =
    unestimated > 0 && amount !== null
      ? `Est. · ${unestimated} ${unestimated === 1 ? "code" : "codes"} not estimated`
      : "Est., Medicare";

  return (
    <FeeRoot title={MEDICARE_ESTIMATE_LABEL}>
      <Typography
        style={{
          fontSize: rem(14),
          fontWeight: 700,
          color: amount === null ? EPOCUS_TEXT_SECONDARY : EPOCUS_TEXT_PRIMARY,
        }}
      >
        {amount === null ? "No estimate" : formatEstimateCents(amount)}
      </Typography>
      {amount !== null && <FeeCaption>{caption}</FeeCaption>}
    </FeeRoot>
  );
};

const metaLine = (row: EpocusBillingDayRowFieldsFragment): string =>
  [row.examLabel, row.identifiers.join(" · "), row.accessionNumber]
    .filter((part): part is string => Boolean(part))
    .join(" · ");

const isReady = (row: EpocusBillingDayRowFieldsFragment): boolean =>
  row.billingStatus === EpocusBillingState.ReadyToBill ||
  row.billingStatus === EpocusBillingState.Exported;

const heldBackLabel = (row: EpocusBillingDayRowFieldsFragment): string => {
  if (row.holdReason) return BILLING_HOLD_REASON_LABELS[row.holdReason];
  if (row.billingStatus === EpocusBillingState.Draft) return "Not signed";
  const [first, ...rest] = row.missingItems;
  if (!first) return "Status pending";
  const label = BILLING_MISSING_ITEM_LABELS[first];
  return rest.length > 0 ? `${label} +${rest.length}` : label;
};

const RowBody: FC<{
  row: EpocusBillingDayRowFieldsFragment;
  showFee: boolean;
}> = ({ row, showFee }) => {
  const billable = isReady(row);
  const cpt = cptChipLabel(row.cptCode, row.modifier);

  return (
    <RowRoot>
      <Mark
        style={{
          backgroundColor: billable
            ? EPOCUS_STATUS_REVIEWED_BG
            : EPOCUS_STATUS_DRAFT_BG,
          color: billable
            ? EPOCUS_STATUS_REVIEWED_TEXT
            : EPOCUS_STATUS_DRAFT_TEXT,
        }}
      >
        {billable ? (
          <Check style={{ fontSize: rem(17) }} titleAccess="Compliant" />
        ) : (
          <PriorityHigh style={{ fontSize: rem(17) }} titleAccess="Held back" />
        )}
      </Mark>

      <Info>
        <Typography
          style={{
            fontSize: rem(15),
            fontWeight: 600,
            color: EPOCUS_TEXT_PRIMARY,
          }}
        >
          {row.patientName ?? "Unnamed patient"}
        </Typography>
        <Meta>{metaLine(row)}</Meta>
      </Info>

      {row.amendedAfterExportAt && (
        <Chip
          style={{
            backgroundColor: EPOCUS_STATUS_DRAFT_BG,
            color: EPOCUS_STATUS_DRAFT_TEXT,
          }}
        >
          Amended
        </Chip>
      )}

      {showFee && <StudyFee estimate={row.feeEstimate} />}

      {row.alreadySent && (
        <Chip
          style={{
            backgroundColor: EPOCUS_SYNCED_BG,
            color: EPOCUS_SYNCED_TEXT,
          }}
        >
          Sent
        </Chip>
      )}

      {billable ? (
        <>
          {cpt && (
            <Chip
              style={{
                backgroundColor: EPOCUS_SELECTED_BG,
                color: EPOCUS_TEXT_PRIMARY,
              }}
            >
              {cpt}
            </Chip>
          )}
          <ChevronRight
            style={{ color: EPOCUS_TEXT_SECONDARY, fontSize: rem(20) }}
          />
        </>
      ) : (
        <Chip
          style={{
            backgroundColor: EPOCUS_STATUS_DRAFT_BG,
            color: EPOCUS_STATUS_DRAFT_TEXT,
            whiteSpace: "normal",
          }}
        >
          {heldBackLabel(row)}
        </Chip>
      )}
    </RowRoot>
  );
};

export const BillingStudyRow: FC<{
  row: EpocusBillingDayRowFieldsFragment;
  showFee: boolean;
}> = ({ row, showFee }) =>
  isReady(row) ? (
    <RowLink to={linkToEpocusBillingReport(row.examId)}>
      <RowBody row={row} showFee={showFee} />
    </RowLink>
  ) : (
    <RowBody row={row} showFee={showFee} />
  );
